import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MessageRepository, MotivationalMessage, InternalMessage } from '../repositories/MessageRepository';
import { WeatherRepository, WeatherData } from '../repositories/WeatherRepository';

/**
 * Hook to fetch the daily motivational message
 * - Cached for 1 hour
 * - Automatically rotates each day
 */
export function useMotivationalMessage() {
  return useQuery<MotivationalMessage | null, Error>({
    queryKey: ['motivational-message'],
    queryFn: () => MessageRepository.getDailyMotivationalMessage(),
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to fetch internal company messages
 * - Cached for 5 minutes
 * - Refetches on window focus
 * - Filters out expired and user-specific messages automatically
 */
export function useInternalMessages() {
  return useQuery<InternalMessage[], Error>({
    queryKey: ['internal-messages'],
    queryFn: () => MessageRepository.getInternalMessages(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to mark an internal message as read
 */
export function useMarkMessageAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (messageId: string) => MessageRepository.markMessageAsRead(messageId),
    onSuccess: () => {
      // Invalidate messages query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['internal-messages'] });
    },
  });
}

/**
 * Hook to fetch weather for all 3 Florida markets
 * - Cached for 30 minutes (matches database cache TTL)
 * - Uses cache-first strategy via WeatherRepository
 * - Automatically refetches every 30 minutes
 */
export function useWeather() {
  return useQuery<WeatherData[], Error>({
    queryKey: ['weather', 'florida-markets'],
    queryFn: () => WeatherRepository.fetchWeather(),
    staleTime: 1000 * 60 * 30, // 30 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
    refetchInterval: 1000 * 60 * 30, // Auto-refetch every 30 minutes
    refetchOnWindowFocus: false,
    retry: 2, // Retry twice on failure
  });
}

/**
 * Combined hook for all Captain Overview data
 * Returns all data sources in a single hook for convenience
 */
export function useCaptainOverviewData() {
  const motivationalMessage = useMotivationalMessage();
  const internalMessages = useInternalMessages();
  const weather = useWeather();

  return {
    motivationalMessage: {
      data: motivationalMessage.data,
      isLoading: motivationalMessage.isLoading,
      error: motivationalMessage.error,
    },
    internalMessages: {
      data: internalMessages.data || [],
      isLoading: internalMessages.isLoading,
      error: internalMessages.error,
    },
    weather: {
      data: weather.data || [],
      isLoading: weather.isLoading,
      error: weather.error,
    },
    isLoading: motivationalMessage.isLoading || internalMessages.isLoading || weather.isLoading,
  };
}
